import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shigo-ui/ui';
import { DashboardPageFormBody } from '@shigo-ui/dashboard-ui';
import { Plus, Trash2 } from 'lucide-react';

import {
  formatMadBuyLineItemCurrency,
  MadBuyFrequencyCappingTimeUnit,
  madBuyCampaignFormStatusOptions,
  madBuyCampaignStatusLabels,
  madBuyLineItemTypeLabels,
  madBuyLineItemTypeOptions,
  type MadBuyCampaignStatus,
  type MadBuyLineItemGeneralForm,
  type MadBuyLineItemType,
} from '../../lineItemsModel';
import { dashboardT } from '../../../../../lib/messages';
import { FormField, ToggleSection } from './LineItemEditorFields';
import type { LineItemFormUpdate } from './LineItemEditorTypes';

export function LineItemGeneralFields({
  budget,
  currency,
  errors,
  form,
  isEditMode,
  submitted,
  updateField,
}: {
  budget: number;
  currency: string;
  errors: Partial<Record<keyof MadBuyLineItemGeneralForm, string>>;
  form: MadBuyLineItemGeneralForm;
  isEditMode: boolean;
  submitted: boolean;
  updateField: LineItemFormUpdate;
}) {
  return (
    <DashboardPageFormBody
      title={dashboardT('madBuy.lineItems.general')}
      description={dashboardT('madBuy.lineItems.generalDescription')}
    >
      <FormField
        label={dashboardT('madBuy.common.name')}
        required
        error={submitted ? errors.name : undefined}
      >
        <Input
          aria-label={dashboardT('madBuy.common.name')}
          value={form.name}
          onChange={(event) => {
            updateField('name', event.target.value);
          }}
        />
      </FormField>

      <div className="grid gap-4 md:grid-cols-2">
        <FormField label={dashboardT('madBuy.common.status')} required>
          <Select
            value={form.status}
            onValueChange={(value) => {
              updateField('status', value as MadBuyCampaignStatus);
            }}
          >
            <SelectTrigger aria-label={dashboardT('madBuy.common.status')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {madBuyCampaignFormStatusOptions.map((status) => (
                <SelectItem key={status} value={status}>
                  {madBuyCampaignStatusLabels[status]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>

        <FormField label={dashboardT('madBuy.lineItems.adType')} required>
          <Select
            value={form.adType}
            disabled={isEditMode || form.lineItemCreatives.length > 0}
            onValueChange={(value) => {
              updateField('adType', value as MadBuyLineItemType);
            }}
          >
            <SelectTrigger aria-label={dashboardT('madBuy.lineItems.adType')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {madBuyLineItemTypeOptions.map((type) => (
                <SelectItem key={type} value={type}>
                  {madBuyLineItemTypeLabels[type]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!isEditMode && form.lineItemCreatives.length > 0 ? (
            <p className="text-muted-foreground text-xs">
              {dashboardT('madBuy.lineItems.removeCreativesBeforeAdTypeChange')}
            </p>
          ) : null}
        </FormField>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <FormField
          label={dashboardT('madBuy.common.cpm')}
          required
          error={submitted ? errors.cpm : undefined}
        >
          <Input
            aria-label={dashboardT('madBuy.common.cpm')}
            inputMode="decimal"
            value={form.cpm}
            onChange={(event) => {
              updateField('cpm', event.target.value);
            }}
          />
        </FormField>
        <FormField
          label={dashboardT('madBuy.lineItems.impressionGoal')}
          required
          error={submitted ? errors.impressionsGoal : undefined}
        >
          <Input
            aria-label={dashboardT('madBuy.lineItems.impressionGoal')}
            inputMode="numeric"
            value={form.impressionsGoal}
            onChange={(event) => {
              updateField('impressionsGoal', event.target.value);
            }}
          />
        </FormField>
        <FormField label={dashboardT('madBuy.lineItems.budget')}>
          <Input
            aria-label={dashboardT('madBuy.lineItems.budget')}
            value={formatMadBuyLineItemCurrency(budget, { currency })}
            readOnly
            tabIndex={-1}
          />
        </FormField>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <FormField
          label={dashboardT('madBuy.lineItems.advertiserDomain')}
          error={submitted ? errors.domain : undefined}
        >
          <Input
            aria-label={dashboardT('madBuy.lineItems.advertiserDomain')}
            value={form.domain}
            onChange={(event) => {
              updateField('domain', event.target.value);
            }}
          />
        </FormField>
        <FormField
          label={dashboardT('madBuy.lineItems.margin')}
          error={submitted ? errors.margin : undefined}
        >
          <Input
            aria-label={dashboardT('madBuy.lineItems.margin')}
            inputMode="decimal"
            value={form.margin}
            onChange={(event) => {
              updateField('margin', event.target.value);
            }}
          />
        </FormField>
      </div>

      <ToggleSection
        label={dashboardT('madBuy.lineItems.upstreamCap')}
        checked={form.upstreamCpmCapEnabled}
        onCheckedChange={(checked) => {
          updateField('upstreamCpmCapEnabled', checked);
        }}
      >
        <div className="grid gap-4 md:grid-cols-2">
          <FormField
            label={dashboardT('madBuy.lineItems.minCpm')}
            error={submitted ? errors.upstreamCpmCapMinCpm : undefined}
          >
            <Input
              aria-label={dashboardT('madBuy.lineItems.minCpm')}
              inputMode="decimal"
              value={form.upstreamCpmCapMinCpm}
              onChange={(event) => {
                updateField('upstreamCpmCapMinCpm', event.target.value);
              }}
            />
          </FormField>
          <FormField
            label={dashboardT('madBuy.lineItems.maxCpm')}
            error={submitted ? errors.upstreamCpmCapMaxCpm : undefined}
          >
            <Input
              aria-label={dashboardT('madBuy.lineItems.maxCpm')}
              inputMode="decimal"
              value={form.upstreamCpmCapMaxCpm}
              onChange={(event) => {
                updateField('upstreamCpmCapMaxCpm', event.target.value);
              }}
            />
          </FormField>
        </div>
      </ToggleSection>

      <ToggleSection
        label={dashboardT('madBuy.creatives.impressionTrackers')}
        checked={form.impressionTrackersEnabled}
        onCheckedChange={(checked) => {
          updateField('impressionTrackersEnabled', checked);
        }}
      >
        <div className="grid gap-3">
          {form.impressionTrackers.map((tracker, index) => (
            // Trackers are plain strings with no id; each row is fully controlled by form state
            // and addressed by index, so no row-local state can follow the wrong tracker.
            // react-doctor-disable-next-line react-doctor/no-array-index-as-key
            <div key={index} className="flex gap-2">
              <Input
                aria-label={dashboardT('madBuy.lineItems.impressionTracker', {
                  index: String(index + 1),
                })}
                value={tracker}
                onChange={(event) => {
                  updateTracker(form, index, event.target.value, updateField);
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                aria-label={dashboardT('madBuy.lineItems.removeImpressionTracker', {
                  index: String(index + 1),
                })}
                onClick={() => {
                  removeTracker(form, index, updateField);
                }}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            className="justify-self-start rounded-full"
            onClick={() => {
              updateField('impressionTrackers', [...form.impressionTrackers, '']);
            }}
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            {dashboardT('madBuy.lineItems.addTracker')}
          </Button>
        </div>
      </ToggleSection>

      <ToggleSection
        label={dashboardT('madBuy.lineItems.frequencyCapping')}
        checked={form.frequencyCappingsEnabled}
        onCheckedChange={(checked) => {
          updateField('frequencyCappingsEnabled', checked);
        }}
      >
        <div className="grid gap-4 md:grid-cols-3">
          <FormField
            label={dashboardT('madBuy.lineItems.limit')}
            error={submitted ? errors.fcLimit : undefined}
          >
            <Input
              aria-label={dashboardT('madBuy.lineItems.frequencyCapLimit')}
              inputMode="numeric"
              value={form.fcLimit}
              onChange={(event) => {
                updateField('fcLimit', event.target.value);
              }}
            />
          </FormField>
          <FormField
            label={dashboardT('madBuy.lineItems.duration')}
            error={submitted ? errors.fcDuration : undefined}
          >
            <Input
              aria-label={dashboardT('madBuy.lineItems.frequencyCapDuration')}
              inputMode="numeric"
              value={form.fcDuration}
              onChange={(event) => {
                updateField('fcDuration', event.target.value);
              }}
            />
          </FormField>
          <FormField label={dashboardT('madBuy.lineItems.unit')}>
            <Select
              value={form.fcTimeUnit}
              onValueChange={(value) => {
                updateField('fcTimeUnit', value as MadBuyFrequencyCappingTimeUnit);
              }}
            >
              <SelectTrigger aria-label={dashboardT('madBuy.lineItems.frequencyCapUnit')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={MadBuyFrequencyCappingTimeUnit.Hour}>
                  {dashboardT('madBuy.lineItems.hour')}
                </SelectItem>
                <SelectItem value={MadBuyFrequencyCappingTimeUnit.Day}>
                  {dashboardT('madBuy.lineItems.day')}
                </SelectItem>
              </SelectContent>
            </Select>
          </FormField>
        </div>
      </ToggleSection>
    </DashboardPageFormBody>
  );
}

function updateTracker(
  form: MadBuyLineItemGeneralForm,
  index: number,
  value: string,
  updateField: LineItemFormUpdate,
) {
  const trackers = [...form.impressionTrackers];
  trackers[index] = value;
  updateField('impressionTrackers', trackers);
}

function removeTracker(
  form: MadBuyLineItemGeneralForm,
  index: number,
  updateField: LineItemFormUpdate,
) {
  const trackers = form.impressionTrackers.filter((_, trackerIndex) => trackerIndex !== index);
  updateField('impressionTrackers', trackers.length ? trackers : ['']);
}
